import React, { useCallback, useEffect, useRef, useState } from 'react';
import { StyledTabItem, StyledTabList, StyledTabPanel, StyledTabItemContainer, StyledTabItemInner, StyledIconButton, StyledOuter, StyledListItem, StyledIcon } from './index.styles.js';
import {} from '../icon/index.js';
import {} from '@oneaudi/unified-web-alpha-common';
import { pickA11yProps } from '../../utils/pick-a11y-props.js';
import { pickDataProps } from '../../utils/pick-data-props.js';
import { useArrowKeyTabs } from '../../hooks/use-arrow-navigation.js';
import { handleScrollInView } from '../../utils/handle-scroll-in-view.js';
import debounce from 'lodash.debounce';
import { handleArrowClick } from './handle-arrow-click.js';
const TabItem = ({ item, id, size, index, showScrollButtons, containerRef }) => {
    const tabRef = useRef(null);
    const tabId = `${id}-tab-btn-${index}`;
    const tabPanelId = `${id}-tab-pnl-${index}`;
    const iconSize = size === 'md' ? 's' : 'xs';
    const tabItemIcon = item.icon && (React.createElement(StyledIcon, { name: item.icon, size: iconSize }));
    useEffect(() => {
        if (!showScrollButtons)
            return;
        if (item.active)
            handleScrollInView({
                containerRef,
                itemRef: tabRef
            });
    }, [item.active, showScrollButtons, containerRef]);
    useEffect(() => {
        const container = containerRef.current;
        if (item.active && container?.contains(document.activeElement)) {
            tabRef.current?.focus();
        }
    }, [item.active, containerRef]);
    return (React.createElement(StyledListItem, { role: "presentation" },
        React.createElement(StyledTabItem, { ref: tabRef, active: item.active, size: size, id: tabId, role: "tab", onClick: item.onClick, "aria-selected": item.active, "aria-controls": tabPanelId, disabled: item.disabled, showScrollButtons: showScrollButtons, tabIndex: item.active ? 0 : -1 },
            React.createElement(StyledTabItemInner, { size: size, active: item.active },
                item.iconPosition === 'leading' && tabItemIcon,
                React.createElement("span", null, item.label),
                item.iconPosition === 'trailing' && tabItemIcon))));
};
export const Tabs = React.forwardRef(({ items, size, id, defaultScrollable, scrollButtons, onKeyNavigate, onClickNavigate, className, margin, ...rest }, ref) => {
    const tabListRef = useRef(null);
    const [isScrollable, setIsScrollable] = useState(false);
    const showScrollButtons = defaultScrollable || isScrollable;
    const a11yProps = pickA11yProps(rest);
    const dataProps = pickDataProps(rest);
    const activeIndex = items.findIndex((item) => item.active);
    /** @deprecated - scrolling behavior is deprecated. Instead, we offer
     * handleArrowClick() on the scroll buttons that should be used similar to the onKeyNavigate prop:
     * to set the next active item.
     */
    const scroll = useCallback((direction) => {
        if (onClickNavigate) {
            if (direction === 'left') {
                return handleArrowClick('left', items, activeIndex, onClickNavigate);
            }
            return handleArrowClick('right', items, activeIndex, onClickNavigate);
        }
        const element = tabListRef.current;
        if (!element) {
            return;
        }
        const multiplier = 0.75;
        const scrollLength = element.offsetWidth * multiplier;
        return element.scrollBy({
            left: direction === 'left' ? -scrollLength : scrollLength,
            behavior: 'smooth'
        });
    }, [activeIndex]);
    const checkScrollable = useCallback(debounce(() => {
        if (!tabListRef.current)
            return;
        const { scrollWidth, clientWidth } = tabListRef.current;
        setIsScrollable(scrollWidth > clientWidth);
    }, 50), [tabListRef]);
    useEffect(() => {
        checkScrollable();
        window.addEventListener('resize', checkScrollable);
        return () => {
            window.removeEventListener('resize', checkScrollable);
        };
    }, [tabListRef]);
    useArrowKeyTabs(tabListRef, items, activeIndex, onKeyNavigate);
    return (React.createElement(StyledOuter, { ...a11yProps, ...dataProps, className: className, ref: ref, margin: margin },
        React.createElement(StyledTabItemContainer, null,
            showScrollButtons && (React.createElement(StyledIconButton, { variant: "tertiary", icon: "back", size: size, "aria-label": scrollButtons.left.ariaLabel, onClick: () => scroll('left') })),
            React.createElement(StyledTabList, { role: "tablist", ref: tabListRef, size: size }, items.map((item, i) => (React.createElement(TabItem, { key: i, item: item, id: id, index: i, size: size, showScrollButtons: showScrollButtons, containerRef: tabListRef })))),
            showScrollButtons && (React.createElement(StyledIconButton, { variant: "tertiary", icon: "forward", size: size, "aria-label": scrollButtons.right.ariaLabel, onClick: () => scroll('right') }))),
        items.map((item, i) => {
            const tabId = `${id}-tab-btn-${i}`;
            const tabPanelId = `${id}-tab-pnl-${i}`;
            return (React.createElement(StyledTabPanel, { key: tabPanelId, active: item.active, role: "tabpanel", id: tabPanelId, "aria-labelledby": tabId, tabIndex: item.active ? 0 : -1 }, item.content));
        })));
});
Tabs.displayName = 'Tabs';
//# sourceMappingURL=index.js.map